// web/src/features/team/player/RosterPlayerContext.tsx
import React, { createContext, useContext } from 'react'
import { App } from 'antd'
import { Formik, FormikHelpers } from 'formik'
import { t } from '@sportninja/common/i18n'
import {
  initialRosterPlayerValues,
  toRosterCreatePayload,
  toRosterCopyPayload,
  toRosterUpdatePayload,
  mapRosterPlayerToForm,
  mapSearchPlayerToForm,
  isRosterEmailLocked,
  isRosterSalesforceIdLocked,
  ROSTER_BE_TO_FORM_FIELD,
  ROSTER_BE_TO_FIELD_TITLE,
  PLAYER_SUCCESS_MESSAGES,
  type RosterMode,
  type RosterPlayerFormValues,
  type RosterPlayerListItem,
  type RosterSearchPlayer,
} from '@sportninja/common/features/team/player'
import { applyPlayerApiError } from '@sportninja/common/features/profile/player'
import { EditPlayerContext } from 'src/features/profile/player/setupModal/EditPlayerContext'
import { scrollToFirstFieldError } from 'src/helpers/scrollToFirstFieldError'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import {
  useCreateRosterPlayer,
  useCopyRosterPlayer,
  useUpdateRosterPlayer,
  useRosterPlayerImageMutation,
  useDefaultPositionId,
} from './hooks/useRosterPlayer'

interface RosterPlayerContextValue {
  isLoading: boolean
  onCancel: () => void
  sportId: string | null
  hasAdminPermission: boolean
  /** Roster requires email → email field is mandatory + shows required. */
  requireEmail: boolean
  /** True when editing an existing roster player (vs adding). Drives the title. */
  isEdit: boolean
  /** Add-existing (copy) mode → personal fields are shown read-only. */
  isAddExisting: boolean
  /**
   * Email is locked when it's the player's settled identity: add-existing
   * (always read-only) or an edited player who has accepted their invite
   * (is_accepted). An invited-but-unaccepted player keeps email editable.
   */
  emailLocked: boolean
  /**
   * The Salesforce ID is owned by Salesforce, so it can only be SET on a
   * brand-new player. Read-only for add-existing and edit.
   */
  salesforceIdLocked: boolean
}

const RosterPlayerContext = createContext<RosterPlayerContextValue | null>(null)

export const useRosterPlayerContext = (): RosterPlayerContextValue => {
  const ctx = useContext(RosterPlayerContext)
  if (!ctx) {
    throw new Error(
      'useRosterPlayerContext must be used within a RosterPlayerProvider'
    )
  }
  return ctx
}

interface RosterPlayerProviderProps {
  teamId: string
  rosterId: string
  sportId: string | null
  sportName?: string
  hasAdminPermission?: boolean
  /** Roster's require_email flag → makes email mandatory + shows required. */
  requireEmail?: boolean
  /**
   * Add-existing mode: the copy target id. Personal fields come from
   * `searchPlayer` (no profile fetch). Ignored when `editPlayer` is provided.
   */
  playerId?: string
  /**
   * Add-existing mode: the picked search row (name/email/image). The form is
   * seeded from this and personal fields are shown read-only; Save copies only
   * the roster fields (the BE keeps the player's own profile).
   */
  searchPlayer?: RosterSearchPlayer
  /**
   * Edit mode: the existing roster player row (from the roster list response).
   * Personal AND roster fields are prefilled from the row (no fetch — the row is
   * complete), all editable, and Save sends the changes to PUT
   * /teams/:team/rosters/:roster/players/:id. Takes precedence over `playerId`.
   */
  editPlayer?: RosterPlayerListItem
  onClose: () => void
  onSuccess?: () => void
  children: React.ReactNode
}

export const RosterPlayerProvider: React.FC<RosterPlayerProviderProps> = ({
  teamId,
  rosterId,
  sportId,
  hasAdminPermission = false,
  requireEmail = false,
  playerId,
  searchPlayer,
  editPlayer,
  onClose,
  onSuccess,
  children,
}) => {
  const { message } = App.useApp()
  const { create, isPending: isCreating } = useCreateRosterPlayer()
  const { copy, isPending: isCopying } = useCopyRosterPlayer()
  const { update, isPending: isUpdating } = useUpdateRosterPlayer()
  const {
    uploadImage,
    deleteImage,
    isPending: isImagePending,
  } = useRosterPlayerImageMutation()
  const defaultPositionId = useDefaultPositionId(sportId)

  const isEdit = Boolean(editPlayer)
  // The player id we save against: edit → the row's id; add-existing → playerId.
  const targetPlayerId = editPlayer?.id ?? playerId

  const isAddExisting = !isEdit && Boolean(searchPlayer ?? playerId)

  const mode: RosterMode = isEdit
    ? 'edit'
    : isAddExisting
    ? 'addExisting'
    : 'create'

  // Lock email only when it's the player's settled identity (add-existing, or an
  // edited player who accepted their invite). Shared rule with mobile.
  const emailLocked = isRosterEmailLocked({ mode, editPlayer })

  // Salesforce owns the id — settable only on a brand-new player.
  const salesforceIdLocked = isRosterSalesforceIdLocked(mode)

  // Formik key: remount on mode/target so opening a different player (or
  // switching between create/add-existing/edit) always re-seeds initial values.
  // Includes defaultPositionId so a late types load still seeds the dropdown.
  const formikKey = `ready:${
    isEdit ? `edit:${targetPlayerId}` : targetPlayerId ?? 'create'
  }:${defaultPositionId}`

  // Initial values:
  // - Edit → prefill personal AND roster fields from the row (no fetch).
  // - Add-existing → seed name/email/image from the picked search row (no
  //   fetch); personal shown read-only, roster fields blank.
  // - Create → fully blank.
  const seededValues: RosterPlayerFormValues = editPlayer
    ? mapRosterPlayerToForm(editPlayer)
    : searchPlayer
    ? mapSearchPlayerToForm(searchPlayer)
    : initialRosterPlayerValues

  // Pre-select the sport's default position whenever the form has none (add-new
  // and add-existing; edit carries the player's own). Without it Position starts
  // blank and a save sends player_type_id: null.
  const initialValues: RosterPlayerFormValues =
    seededValues.positionId === '' && defaultPositionId !== ''
      ? { ...seededValues, positionId: defaultPositionId }
      : seededValues

  const isLoading = isCreating || isCopying || isUpdating || isImagePending

  const handleSubmit = async (
    values: RosterPlayerFormValues,
    helpers: FormikHelpers<RosterPlayerFormValues>
  ) => {
    // Edit → diff PUT; add-existing copy → roster fields only (BE keeps the
    // player's profile); create-new → full new-player body.
    const body = isEdit
      ? toRosterUpdatePayload(values, initialValues)
      : playerId
      ? toRosterCopyPayload(values)
      : toRosterCreatePayload(values)
    try {
      // Edit → PUT update (existing row id). Add-existing → copy (searched id).
      // Create-new → POST create. All return the roster player record.
      const saved = editPlayer
        ? await update({
            teamId,
            rosterId,
            playerId: editPlayer.id,
            body,
          })
        : playerId
        ? await copy({ teamId, rosterId, playerId, body })
        : await create({ teamId, rosterId, body })

      // Image step AFTER save (needs the player id). Edit → row id; add-existing
      // → searched id; create → the new record's id. A failure leaves the player
      // on the roster — surface a toast, don't abort.
      const imagePlayerId = targetPlayerId ?? saved?.id
      if (values.imageData && imagePlayerId) {
        try {
          await uploadImage({
            teamId,
            playerId: imagePlayerId,
            file: values.imageData,
          })
        } catch {
          message.error(`${t('errors:imageUploadProblem', {})}.`)
        }
      } else if (isEdit && values.imageDeleted && imagePlayerId) {
        // Edit only: the user removed the existing avatar.
        try {
          await deleteImage({ teamId, playerId: imagePlayerId })
        } catch {
          message.error(`${t('errors:imageUploadProblem', {})}.`)
        }
      }

      message.success(
        formatSuccessToast(
          isEdit
            ? PLAYER_SUCCESS_MESSAGES.rosterEdit
            : PLAYER_SUCCESS_MESSAGES.rosterAdd
        )
      )
      onSuccess?.()
      onClose()
    } catch (err: unknown) {
      applyPlayerApiError(err, {
        fieldMap: ROSTER_BE_TO_FORM_FIELD,
        titleMap: ROSTER_BE_TO_FIELD_TITLE,
        setErrors: helpers.setErrors,
        setTouched: (touched) => helpers.setTouched(touched, false),
        notifyError: message.error,
        // Bring the first errored field into view + focus it.
        onFieldErrors: () => scrollToFirstFieldError(),
      })
    } finally {
      helpers.setSubmitting(false)
    }
  }

  return (
    <Formik<RosterPlayerFormValues>
      key={formikKey}
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleSubmit}
    >
      <RosterPlayerContext.Provider
        value={{
          isLoading,
          onCancel: onClose,
          sportId,
          hasAdminPermission,
          requireEmail,
          isEdit,
          isAddExisting,
          emailLocked,
          salesforceIdLocked,
        }}
      >
        {/* Supply an EditPlayerContext value so the reused profile sections
            (PersonalSection reads useEditPlayerContext) work. The target id is
            passed through so add-existing/edit show the player ID chip; create
            has none. */}
        <EditPlayerContext.Provider
          value={{ isLoading, onCancel: onClose, playerId: targetPlayerId }}
        >
          {children}
        </EditPlayerContext.Provider>
      </RosterPlayerContext.Provider>
    </Formik>
  )
}
